import groq from 'groq'

import { CategoryPathUrls } from '~/bootcamp/types/bootcamp.types'
import appConfig from '~/config/app'
import { theoryTopicFields } from '~/shared/queries/theory'

const isPublishedQuery = appConfig.isPublicWebsite ? '&& isPublished' : ''

export const firstSubjectKeyGroq = groq`
coalesce(*[_type == "bootcamp" && language == $language && gymCategory == $gymCategory ] | order(order asc) {
  key,
  "modules": count(*[_type == "bootcampModule" && references(^._id) ${isPublishedQuery}].moduleNumber)
}{
  "subjectKey": select(modules > 0 => key,"")
} [0],{"subjectKey" : ""})`

export const subjectListWithCourseListGroq = groq`
{
  "subjects": *[_type == "bootcamp" && language == $language && gymCategory == $gymCategory ] | order(order asc) {
  _id, name, key,
  tagStatus{tag, validity},
  "state": coalesce(state,'unknown'),
  "modules": count(*[_type == "bootcampModule" && references(^._id) ${isPublishedQuery}].moduleNumber),
  "serial": order,
  "totalPoints":coalesce(totalPoints, 0),
  "chests": *[_type == "bootcampModule" && references(^._id) ${isPublishedQuery}] | order(moduleNumber asc){
    moduleNumber,
    "pages": pages[@->isHidden != true]->{
      "activities": activities[]->{
        "difficulties": difficulties[]{
          "rewards": coalesce(rewards, 0)
        }[].rewards
      }
    }
  },
  "courses":*[_type == "bootcamp" && key == ^.key && language == $language][0] {
    name,
    optionalTitle,
    intro,
    "metaDescription": coalesce(metaDescription, ^.key),
    "modules": *[_type == "bootcampModule" && references(^._id) ${isPublishedQuery}] | order(moduleNumber asc){
      "taskReferences": *[_type == "mappingQuizTask" && references(^._id)][0]{
        mathPreview
      },
      "name": coalesce(name,''),
      moduleNumber,
      tagStatus{tag, validity},
      "badgeImage": {
        "alt": coalesce(badgeImage.alt, ''),
        "srcKey": coalesce(badgeImage.srcKey, '')
      },
      "badgeOutlineImage": {
        "alt": coalesce(badgeOutlineImage.alt, ''),
        "srcKey": coalesce(badgeOutlineImage.srcKey, '')
      },
      "levels": count(pages[defined(@->activities) && @->isHidden != true]),
      "chests": pages[@->isHidden != true]->{
        activities[]->{
          "difficulties": difficulties[]{
            "rewards": coalesce(rewards, 0)
          }[].rewards
        }
      },
      "prerequisites": prerequisites[]{
        "name": coalesce(name, ''),
        "url": coalesce(url, '')
      },
      "hasWhyLevel":coalesce(hasWhyLevel,true),
      "firstPageData": pages[@->isHidden != true][0]->{
        level,
        "regular": defined(activities),
        shortTitle,
        "hasEncyclopedia": defined(encyclopediaArticle) || count(howContent[defined(_type) && _type == "awsVideo" && defined(srcKey)]) == 0,        
      },
    },
  },
  "superBadgeOutlineImage": {
    "alt": coalesce(badgeOutlineImage.alt, ''),
    "srcKey": coalesce(badgeOutlineImage.srcKey, ''),
  },
  "superBadgePartImages": *[_type == "bootcampModule" && references(^._id) ${isPublishedQuery}]| order(moduleNumber asc){
    "bootcampNumber": moduleNumber,
    "alt": coalesce(superBadgePartImage.alt, ''),
    "srcKey": coalesce(superBadgePartImage.srcKey, ''),
    "id": _id
  },
}[modules > 0 && count(chests) > 0],
"reference": *[_type == "bootcamp" && language == "en" && gymCategory == $gymCategory ] | order(order asc) {
  key,
  name,
  "modules": count(*[_type == "bootcampModule" && references(^._id) ${isPublishedQuery}].moduleNumber),
  "chests": *[_type == "bootcampModule" && references(^._id) ${isPublishedQuery}] | order(moduleNumber asc){
    moduleNumber,
    "pages": pages[@->isHidden != true]->{
      "activities": activities[]->{
        "difficulties": difficulties[]{
          "rewards": coalesce(rewards, 0)
        }[].rewards
      }
    }
  },
  "superBadgeOutlineImage": {
    "alt": coalesce(badgeOutlineImage.alt, ''),
    "srcKey": coalesce(badgeOutlineImage.srcKey, ''),
  },
  }[modules > 0 && count(chests) > 0]{
    key, superBadgeOutlineImage, name
  },
}`

export const overviewGroq = groq`
{
  "bootcamps":coalesce(
    *[_type == "bootcamp" && key == $key && language == $language][0] {
      name,
      optionalTitle,
      intro,
      "metaDescription": coalesce(metaDescription, $key),
      "modules": *[_type == "bootcampModule" && references(^._id) ${isPublishedQuery}] | order(moduleNumber asc){ 
        "reference": *[_type == "mappingQuizTask" && references(^._id)][0]{
          mathPreview
        },
        "name": coalesce(name,''),
        moduleNumber,
        tagStatus{tag, validity},
        "badgeImage": {
          "alt": coalesce(badgeImage.alt, ''),
          "srcKey": coalesce(badgeImage.srcKey, '')
        },
        "badgeOutlineImage": {
          "alt": coalesce(badgeOutlineImage.alt, ''),
          "srcKey": coalesce(badgeOutlineImage.srcKey, '')
        },
        "levels": count(pages[defined(@->activities) && @->isHidden != true]),
        "chests": pages[@->isHidden != true]->{
          activities[]->{
            "difficulties": difficulties[]{
              "rewards": coalesce(rewards, 0)
            }[].rewards
          }
        },
        "prerequisites": prerequisites[]{
          "name": coalesce(name, ''),
          "url": coalesce(url, '')
        },
        "hasWhyLevel":coalesce(hasWhyLevel,true),
        "firstPageData": pages[@->isHidden != true][0]->{
          level,
          "regular": defined(activities),
          shortTitle
        },
      },
    },
    {"metaDescription": $key}
  ),
  "reference":*[_type == "bootcamp" && key == $key && language == "en"][0] {
    "modules": *[_type == "bootcampModule" && references(^._id) ${isPublishedQuery}] | order(moduleNumber asc){
      moduleNumber,
      name,
      "badgeOutlineImage": {
        "alt": coalesce(badgeOutlineImage.alt, ''),
        "srcKey": coalesce(badgeOutlineImage.srcKey, '')
      },
      "hasWhyLevel":coalesce(hasWhyLevel,true),
      "firstPageData": pages[@->isHidden != true][0]->{
        level,
        "regular": defined(activities),
        shortTitle
      },
    },
  },
}
`

export const gemsOfModuleGroq = `
  *[_type == "bootcamp" && key == $key && language == $language][0] {
    "module": *[_type == "bootcampModule" && references(^._id) && moduleNumber == $module ${isPublishedQuery}][0]{
      pages[defined(@->activities) && @->isHidden != true]->{
        level,
        "activities": activities[]->{
          type,
          "difficulties":difficulties[]{
            rewards
          }.rewards
        }
      }
    }
  }.module.pages
`
export const bootcampBossGroq = groq`
*[_type == "bootcamp" && key == $key && language == $language][0] {
  bossBattleEnemy,
  "module": *[_type == "bootcampModule" && references(^._id) && moduleNumber == $module ${isPublishedQuery}][0]{
    "taskReference": *[_type == "mappingQuizTask" && references(^._id)][0]{
      taskId
    },
    "badgeImage": {
      "alt": coalesce(badgeImage.alt, ''),
      "srcKey": coalesce(badgeImage.srcKey, ''),
    },
    "levels": pages[defined(@->activities) && @->isHidden != true]->{
      level,
      "activities": activities[]->|order(type asc){
        "activityType": type,
        "activityWeight": coalesce(activityWeight, 1),
        "difficulties": difficulties[]|order(difficultyLevel asc){
          difficultyLevel,
          questionType,
          description,
          "activityName": activityName->name,
          "keyboardLayout": keyboardLayout->layoutName,
          multipleChoiceActivity,
          fillInTheBlankActivity,
          circleCombinationsActivity,
          measureTheItemActivity,
          categorizationActivity,
          matchingPairsActivity,
          orderElementsActivity,
          numberLineActivity,
        }
      }
    }
  }
}{
  "levels": module.levels,
  "badgeImage": module.badgeImage,
  "taskId": module.taskReference.taskId,
  bossBattleEnemy,
}
`

const createCategoryDataGroq = (categoryName: CategoryPathUrls): string => {
  return `*[
    _type == "bootcamp" &&
    gymCategory=="${categoryName}" &&
    language==$language &&
    key != "placeholder" &&
    !(_id in path('drafts.**'))
  ] | order(order asc){
  	_id,
  	key,
    "chests": *[_type == "bootcampModule" && references(^._id) ${isPublishedQuery}] | order(moduleNumber asc){
        "pages": pages[@->isHidden != true]->{
          "activities": activities[]->{
            "difficulties": difficulties[]{
              "rewards": coalesce(rewards, 0)
            }[].rewards
          }
        },
        "levelActivities":count(pages[defined(@->activities) && count(@->activities) > 0 && @->isHidden != true]->activities[]->),
        "whyVideo":select(
          hasWhyLevel == false => 0,
          1
        ),
        "pagesVideo":count(pages[@->isHidden != true])
    }
  }{
      _id,
      key,
      chests,
      "noOfActivities":math::sum(chests[].levelActivities),
      "totalWhyVideo":math::sum(chests[].whyVideo),
      "totalPagesVideo":math::sum(chests[].pagesVideo)
  }{
      _id,
      key,
      chests,
      noOfActivities,
      "noOfVideo":math::sum([totalWhyVideo,totalPagesVideo])
    }[count(chests) > 0]`
}

export const categoriesDataGroq = groq`
  {
    'numbers-and-quantities':${createCategoryDataGroq('numbers-and-quantities')},
    'algebra': ${createCategoryDataGroq('algebra')},
    'geometry': ${createCategoryDataGroq('geometry')},
    'functions': ${createCategoryDataGroq('functions')},
    'proofs': ${createCategoryDataGroq('proofs')},
    'statistics-and-probability': ${createCategoryDataGroq('statistics-and-probability')},
  }
`

export const subjectNameByKeyAndCategoryGroq = groq`
  *[_type == "bootcamp" && language == $language && gymCategory == $bootcampCategory && key == $key][0]{
  key,
  name,
  "defaultName": *[_type == "bootcamp" && language == 'en' && gymCategory == $bootcampCategory && key == $key][0].name
}
`

export const getThumbnailGroq = groq`
*[_type == "bootcamp" && key == $key && language == $language][0] {
  "module": *[_type == "bootcampModule" && references(^._id) && moduleNumber == $module][0]{
    name,
    whyName,
    howContent[_type == "awsVideo"][0]{
      thumbnailUrl,
      description
    },
    "page": pages[@->level == $level && @.isHidden != true]->{
      level,
      name,
      "isDefinition": count(activities) == null,
      howContent[_type == "awsVideo"][0]{
        thumbnailUrl,
        description
      },
    },
  },
}{
  module
}
`

export const overallGroq = groq`
  *[_type == "bootcamp" && key == $key && language == $language][0] {
    name,
    "module": *[_type == "bootcampModule" && references(^._id) && moduleNumber == $module ${isPublishedQuery}][0]{
      name,
      whyName,
      moduleNumber,
      "complexityLevel": coalesce(complexityLevel, 1),
      metaDescription,
      shortTitle,
      "hasWhyLevel": coalesce(hasWhyLevel,true),
      "levelCount": count(pages[defined(@->activities) && @->isHidden != true]),
      "levelsPageData": pages[@->isHidden != true]->{
        level,
        "regular": defined(activities),
        "shortTitle": coalesce(shortTitle, name),
        "hasEncyclopedia": defined(encyclopediaArticle) || count(howContent[defined(_type) && _type == "awsVideo" && defined(srcKey)]) == 0,        
      },
      "levelActivities": pages[@->isHidden != true ]->{
        level,
        "numOfActivities": count(activities),
      },
      "howContent": howContent[_type != "contentPrompt"],
      "encyclopediaArticle": encyclopediaArticle->{${theoryTopicFields}},
      assets{
          help,
          chest,
          "usefulWords":usefulWordsDictionary[]->[$language]{term,link,definition}
      },
      "firstPageData": pages[@->isHidden != true][0]->{
        level,
        "regular": defined(activities),
        shortTitle,
        "hasEncyclopedia": defined(encyclopediaArticle) || count(howContent[defined(_type) && _type == "awsVideo" && defined(srcKey)]) == 0,        
      },
      "page": pages[@->isHidden != true && @->level == $level]->{
        name,
        "howContent": howContent[_type != "contentPrompt"],
        "encyclopediaArticle": encyclopediaArticle->{${theoryTopicFields}},
        level,
        metaDescription,
        metaDescriptionActivity,
        "activities": activities[]-> |order(type asc){
          type,
          "gemType": 'red',
          "workLoadLevel": coalesce(workLoadLevel, 1),
          "difficulties": difficulties[]|order(difficultyLevel asc){
            "activityName": activityName->name,
            "keyboardLayout": keyboardLayout->layoutName,
            description,
            difficultyLevel,
            questionType,
            multipleChoiceActivity,
            fillInTheBlankActivity,
            circleCombinationsActivity,
            measureTheItemActivity,
            categorizationActivity,
            matchingPairsActivity,
            orderElementsActivity,
            numberLineActivity,
            "rewards": select(
              rewards == null => 1,
              rewards > 0 => rewards
            )
          },
        },
        assets{
            help,
            chest,
            "usefulWords":usefulWordsDictionary[]->[$language]{term,link,definition}
        }
      },
      "badgeImage": {
        "alt": coalesce(badgeImage.alt, ''),
        "srcKey": coalesce(badgeImage.srcKey, '')
      },
      "badgeOutlineImage": {
        "alt": coalesce(badgeOutlineImage.alt, ''),
        "srcKey": coalesce(badgeOutlineImage.srcKey, '')
      },
    },
    "moduleCount": count(*[_type == "bootcampModule" && references(^._id) ${isPublishedQuery}])
  }{
    name,
    module,
    moduleCount
  }`
